import {
  MenuButton as MenuButtonComponent,
  MenuButtonProps,
  useMenuContext,
} from '@chakra-ui/menu';
import { EventKeyMap } from '@chakra-ui/react-utils';
import { forwardRef, useMultiStyleConfig } from '@chakra-ui/system';
import { callAllHandlers, cx, dataAttr, normalizeEventKey } from '@chakra-ui/utils';
import { ArrowDownBold } from '@lego/klik-ui-icons';
import { IInputProps, Input } from '@lego/klik-ui-input';
import * as React from 'react';
import { IDropdownSizes } from './DropdownTypes';

// internal component

interface IDropdownInputProps extends IInputProps {
  isOpen: boolean;
  width?: string;
  ariaLabel?: string;
}

const DropdownInput = forwardRef<IDropdownInputProps, 'input'>(
  (
    {
      size,
      isOpen,
      isDisabled,
      value,
      width,
      ariaLabel,
      children: _children,
      id: _id,
      isRequired: _isRequired,
      ...rest
    },
    ref
  ) => {
    return (
      <Input.Group size={size} width={width}>
        <Input
          {...rest}
          aria-label={ariaLabel}
          isDisabled={isDisabled}
          isRequired={false}
          ref={ref}
          role="button"
          type="button"
          value={value}
        />
        <Input.RightElement pointerEvents="none">
          <ArrowDownBold
            data-icon-fixed={true}
            opacity={isDisabled ? 0.5 : 1}
            transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
            transition="transform 0.05s"
          />
        </Input.RightElement>
      </Input.Group>
    );
  }
);

export interface IDropdownButtonProps extends MenuButtonProps {
  size: IDropdownSizes;
  hasInlineSelection: boolean;
  label: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  ariaLabel?: string;
  selectedIndex?: number;
  variant?: string;
}

export const DropdownButton = forwardRef<IDropdownButtonProps, 'input'>(({ ...props }, ref) => {
  const {
    className = 'klik-dropdown',
    isDisabled = false,
    isInvalid,
    size = 'md',
    hasInlineSelection = false,
    label,
    ariaLabel = props.label,
    onClick: onClickProp,
    selectedIndex = -1,
    variant,
    width,
  } = props;

  const menu = useMenuContext();
  const { isOpen, onToggle, onClose, setFocusedIndex } = menu;

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      onToggle();
    },
    [onToggle]
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      const eventKey = normalizeEventKey(e);

      const closeIfOpen = isOpen ? onClose : undefined;

      const keyMap: EventKeyMap = {
        Enter: onToggle,
        Space: onToggle,
        ' ': onToggle,
        Escape: closeIfOpen,
        Tab: closeIfOpen,
        // open and set selection by arrow.up/down too:
        ArrowDown: onToggle,
        ArrowUp: onToggle,
      };

      const action = keyMap[eventKey];

      if (action) {
        e.preventDefault();
        e.stopPropagation();
        action(e);
      }
    },
    [onToggle, onClose, isOpen]
  );

  React.useEffect(() => {
    if (selectedIndex !== -1) {
      if (isOpen) {
        setFocusedIndex(selectedIndex);
      }
    }
  }, [isOpen]);

  const styles = useMultiStyleConfig('Dropdown', { isInvalid, isDisabled, variant, size });

  if (variant === 'borderless') {
    styles.input.borderColor = 'transparent';
    styles.input.boxShadow = 'none';
  }

  return (
    <MenuButtonComponent
      ariaLabel={ariaLabel}
      as={DropdownInput}
      className={cx('klik-ui-dropdown__button', className)}
      data-hasinlineselection={dataAttr(hasInlineSelection)}
      isDisabled={isDisabled}
      isOpen={isOpen}
      onClick={callAllHandlers(onClickProp, handleClick)}
      onKeyDown={handleKeyDown}
      ref={ref}
      size={size}
      sx={styles.input}
      tabIndex={0}
      value={label}
      width={width}
    />
  );
});
